import { useState } from 'react';
import { Copy, Check, X, FileCode } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { RepoFile } from '@/types/git';

interface FileViewerProps {
  file: RepoFile | null;
  className?: string;
  onClose?: () => void;
}

/**
 * Компонент для просмотра содержимого файла
 * Поддерживает подсветку синтаксиса для кода
 */
export function FileViewer({ file, className, onClose }: FileViewerProps) {
  const [copied, setCopied] = useState(false);

  if (!file) {
    return (
      <div
        className={cn('flex flex-col items-center justify-center h-full text-slate-500', className)}
      >
        <FileCode className="h-12 w-12 mb-2 opacity-50" />
        <span className="text-sm">Select a file to view its contents</span>
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(file.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const language = getLanguage(file.path);
  const lineCount = file.content.split('\n').length;

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700 bg-slate-900/50">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-300 truncate">{file.path}</span>
          <span
            className={cn(
              'text-xs px-1.5 py-0.5 rounded',
              file.status === 'committed' && 'bg-slate-500/20 text-slate-400',
              file.status === 'modified' && 'bg-amber-500/20 text-amber-400',
              file.status === 'staged' && 'bg-green-500/20 text-green-400',
              file.status === 'untracked' && 'bg-slate-500/20 text-slate-400'
            )}
          >
            {file.status}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={handleCopy}
            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded transition-colors"
            title="Copy content"
          >
            {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
          </button>

          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded transition-colors"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="flex">
          {/* Line numbers */}
          <div className="flex-shrink-0 py-3 px-2 text-right text-slate-600 text-xs font-mono select-none border-r border-slate-700/50 bg-slate-900/30">
            {Array.from({ length: lineCount }, (_, i) => (
              <div key={i + 1} className="leading-6">
                {i + 1}
              </div>
            ))}
          </div>

          {/* Code content */}
          <pre
            className={cn(
              'flex-1 py-3 px-4 text-sm font-mono leading-6 overflow-x-auto',
              'text-slate-300'
            )}
          >
            <code className={`language-${language}`}>{highlightCode(file.content, language)}</code>
          </pre>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-3 py-1.5 border-t border-slate-700 bg-slate-900/50 text-xs text-slate-500">
        <span>{lineCount} lines</span>
        <span>{formatBytes(file.content.length)}</span>
      </div>
    </div>
  );
}

function getLanguage(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();

  switch (ext) {
    case 'ts':
    case 'tsx':
      return 'typescript';
    case 'js':
    case 'jsx':
      return 'javascript';
    case 'json':
      return 'json';
    case 'md':
      return 'markdown';
    case 'css':
      return 'css';
    case 'html':
      return 'html';
    case 'yaml':
    case 'yml':
      return 'yaml';
    default:
      return 'text';
  }
}

/**
 * Простая подсветка синтаксиса
 * В реальном проекте стоит использовать библиотеку вроде prism.js или highlight.js
 */
function highlightCode(code: string, language: string): React.ReactNode {
  // Базовая подсветка для TypeScript/JavaScript
  if (['typescript', 'javascript', 'json'].includes(language)) {
    return code.split('\n').map((line, i) => (
      <div key={i} className="leading-6">
        {highlightLine(line, language)}
      </div>
    ));
  }

  return code;
}

function highlightLine(line: string, _language: string): React.ReactNode {
  // Простая подсветка ключевых слов
  const keywords = [
    'const',
    'let',
    'var',
    'function',
    'return',
    'if',
    'else',
    'for',
    'while',
    'class',
    'interface',
    'type',
    'import',
    'export',
    'from',
    'async',
    'await',
    'try',
    'catch',
    'throw',
    'new',
    'this',
    'extends',
    'implements',
  ];
  const types = [
    'string',
    'number',
    'boolean',
    'void',
    'null',
    'undefined',
    'any',
    'never',
    'unknown',
    'Promise',
    'Array',
    'Map',
    'Set',
  ];

  let result = line;

  // Подсветка строк
  if (line.includes('"') || line.includes("'") || line.includes('`')) {
    result = result.replace(
      /(["'`])(?:(?!\1)[^\\]|\\.)*\1/g,
      '<span class="text-green-400">$&</span>'
    );
  }

  // Подсветка комментариев
  if (line.trim().startsWith('//') || line.trim().startsWith('*') || line.trim().startsWith('/*')) {
    return <span className="text-slate-500">{line}</span>;
  }

  // Подсветка ключевых слов
  for (const keyword of keywords) {
    const regex = new RegExp(`\\b${keyword}\\b`, 'g');
    result = result.replace(regex, '<span class="text-purple-400">$&</span>');
  }

  // Подсветка типов
  for (const type of types) {
    const regex = new RegExp(`\\b${type}\\b`, 'g');
    result = result.replace(regex, '<span class="text-cyan-400">$&</span>');
  }

  // Подсветка чисел
  result = result.replace(/\b(\d+)\b/g, '<span class="text-amber-400">$1</span>');

  return <span dangerouslySetInnerHTML={{ __html: result }} />;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
